/**
 * Incident Selectors - Phase 13
 * 
 * Filtering, sorting, and grouping helpers for incident data.
 * Reusable across dashboard, detail pages, and export functions.
 */

import {
  INCIDENT_STATUS,
  SEVERITY,
  DOMAINS,
  SOURCE_TYPES,
  FOLLOWUP_STATUS,
  isIncidentOpen,
  isIncidentCritical
} from './incidentModel';
import { getCachedIncidents, aggregateAllIncidents, getIncidentSummary } from './incidentStore';

// ==================== FILTER HELPERS ====================

/**
 * @typedef {Object} IncidentFilters
 * @property {string|null} status - pending, failed, conflict, resolved
 * @property {string|null} severity - info, warning, error, critical
 * @property {string|null} domain - timeclock, orders, payments, etc.
 * @property {string|null} sourceType - staff, backoffice, pos, system
 * @property {string|null} locationId
 * @property {string|null} terminalId
 * @property {string|null} actorUserId
 * @property {number|null} dateFrom - timestamp
 * @property {number|null} dateTo - timestamp
 * @property {string|null} searchText
 * @property {boolean} unresolvedOnly
 * @property {string|null} followUpStatus
 */

/**
 * Apply filters to incidents array
 * @param {Array} incidents
 * @param {IncidentFilters} filters
 * @returns {Array}
 */
export function applyFilters(incidents, filters = {}) {
  return incidents.filter(incident => {
    // Status filter
    if (filters.status && incident.status !== filters.status) {
      return false;
    }
    
    // Severity filter
    if (filters.severity && incident.severity !== filters.severity) {
      return false;
    }
    
    // Domain filter
    if (filters.domain && incident.domain !== filters.domain) {
      return false;
    }
    
    // Source type filter
    if (filters.sourceType && incident.sourceType !== filters.sourceType) {
      return false;
    }
    
    // Location filter
    if (filters.locationId && incident.locationId !== filters.locationId) {
      return false;
    }
    
    // Terminal filter
    if (filters.terminalId && incident.terminalId !== filters.terminalId) {
      return false;
    }
    
    // Actor filter
    if (filters.actorUserId && incident.actor?.userId !== filters.actorUserId) {
      return false;
    }
    
    // Date range filters
    if (filters.dateFrom && incident.occurredAt < filters.dateFrom) {
      return false;
    }
    if (filters.dateTo && incident.occurredAt > filters.dateTo) {
      return false;
    }
    
    // Unresolved only filter
    if (filters.unresolvedOnly && !isIncidentOpen(incident)) {
      return false;
    }
    
    // Follow-up status filter
    if (filters.followUpStatus && incident.followUpStatus !== filters.followUpStatus) {
      return false;
    }
    
    // Search text filter (searches title, message, tags)
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      const searchable = [
        incident.title,
        incident.message,
        incident.eventType,
        incident.actor?.name,
        incident.locationId,
        incident.terminalId,
        ...(incident.tags || [])
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchable.includes(search)) {
        return false;
      }
    }
    
    return true;
  });
}

// ==================== SORT HELPERS ====================

/**
 * Sort options
 */
export const SORT_OPTIONS = {
  NEWEST_FIRST: 'newest_first',
  OLDEST_FIRST: 'oldest_first',
  SEVERITY_DESC: 'severity_desc',
  UNRESOLVED_FIRST: 'unresolved_first'
};

const SEVERITY_ORDER = {
  [SEVERITY.CRITICAL]: 0,
  [SEVERITY.ERROR]: 1,
  [SEVERITY.WARNING]: 2,
  [SEVERITY.INFO]: 3
};

/**
 * Sort incidents
 * @param {Array} incidents
 * @param {string} sortBy
 * @returns {Array}
 */
export function sortIncidents(incidents, sortBy = SORT_OPTIONS.NEWEST_FIRST) {
  const sorted = [...incidents];
  
  switch (sortBy) {
    case SORT_OPTIONS.NEWEST_FIRST:
      sorted.sort((a, b) => b.occurredAt - a.occurredAt);
      break;
      
    case SORT_OPTIONS.OLDEST_FIRST:
      sorted.sort((a, b) => a.occurredAt - b.occurredAt);
      break;
      
    case SORT_OPTIONS.SEVERITY_DESC:
      sorted.sort((a, b) => {
        const severityDiff = (SEVERITY_ORDER[a.severity] || 99) - (SEVERITY_ORDER[b.severity] || 99);
        if (severityDiff !== 0) return severityDiff;
        return b.occurredAt - a.occurredAt; // Secondary sort by time
      });
      break;
      
    case SORT_OPTIONS.UNRESOLVED_FIRST:
      sorted.sort((a, b) => {
        const aOpen = isIncidentOpen(a) ? 0 : 1;
        const bOpen = isIncidentOpen(b) ? 0 : 1;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return b.occurredAt - a.occurredAt; // Secondary sort by time
      });
      break;
      
    default:
      sorted.sort((a, b) => b.occurredAt - a.occurredAt);
  }
  
  return sorted;
}

// ==================== GROUPING HELPERS ====================

/**
 * Group incidents by a key
 * @param {Array} incidents
 * @param {string} groupKey - 'domain', 'sourceType', 'locationId', 'severity', 'date'
 * @returns {Object} - { [groupValue]: incidents[] }
 */
export function groupIncidents(incidents, groupKey) {
  const groups = {};
  
  for (const incident of incidents) {
    let key;
    
    switch (groupKey) {
      case 'domain':
        key = incident.domain || 'other';
        break;
      case 'sourceType':
        key = incident.sourceType || 'unknown';
        break;
      case 'locationId':
        key = incident.locationId || 'no-location';
        break;
      case 'terminalId':
        key = incident.terminalId || 'no-terminal';
        break;
      case 'severity':
        key = incident.severity || 'info';
        break;
      case 'status':
        key = incident.status || 'unknown';
        break;
      case 'date':
        // Group by date (YYYY-MM-DD)
        key = new Date(incident.occurredAt).toISOString().split('T')[0];
        break;
      default:
        key = incident[groupKey] || 'other';
    }
    
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(incident);
  }
  
  return groups;
}

/**
 * Group incidents by time buckets
 * @param {Array} incidents
 * @returns {Object} - { today: [], yesterday: [], thisWeek: [], older: [] }
 */
export function groupIncidentsByTimeBucket(incidents) {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const yesterdayStart = todayStart - day;
  const weekStart = todayStart - 6 * day;
  
  const buckets = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };
  
  for (const incident of incidents) {
    const ts = incident.occurredAt;
    
    if (ts >= todayStart) {
      buckets.today.push(incident);
    } else if (ts >= yesterdayStart) {
      buckets.yesterday.push(incident);
    } else if (ts >= weekStart) {
      buckets.thisWeek.push(incident);
    } else {
      buckets.older.push(incident);
    }
  }
  
  return buckets;
}

// ==================== SELECTOR FUNCTIONS ====================

/**
 * Get all incidents with optional filters
 * @param {IncidentFilters} filters
 * @returns {Promise<Array>}
 */
export async function getAllIncidents(filters = {}) {
  const incidents = await getCachedIncidents();
  const filtered = applyFilters(incidents, filters);
  return sortIncidents(filtered, filters.sortBy || SORT_OPTIONS.NEWEST_FIRST);
}

/**
 * Get incident by ID
 * @param {string} incidentId
 * @returns {Promise<Object|null>}
 */
export async function getIncidentById(incidentId) {
  const incidents = await getCachedIncidents();
  return incidents.find(i => i.incidentId === incidentId) || null;
}

/**
 * Get incidents by domain
 * @param {string} domain
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsByDomain(domain, additionalFilters = {}) {
  return getAllIncidents({ ...additionalFilters, domain });
}

/**
 * Get incidents by severity
 * @param {string} severity
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsBySeverity(severity, additionalFilters = {}) {
  return getAllIncidents({ ...additionalFilters, severity });
}

/**
 * Get incidents by location
 * @param {string} locationId
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsByLocation(locationId, additionalFilters = {}) {
  return getAllIncidents({ ...additionalFilters, locationId });
}

/**
 * Get incidents by terminal
 * @param {string} terminalId
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsByTerminal(terminalId, additionalFilters = {}) {
  return getAllIncidents({ ...additionalFilters, terminalId });
}

/**
 * Get incidents by actor
 * @param {string} userId
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsByActor(userId, additionalFilters = {}) {
  return getAllIncidents({ ...additionalFilters, actorUserId: userId });
}

/**
 * Get incident timeline (grouped by time buckets)
 * @param {IncidentFilters} filters
 * @returns {Promise<Object>}
 */
export async function getIncidentTimeline(filters = {}) {
  const incidents = await getAllIncidents(filters);
  return groupIncidentsByTimeBucket(incidents);
}

/**
 * Get count of open incidents
 * @param {IncidentFilters} filters
 * @returns {Promise<number>}
 */
export async function getOpenIncidentCount(filters = {}) {
  const incidents = await getAllIncidents({ ...filters, unresolvedOnly: true });
  return incidents.length;
}

/**
 * Get count of resolved incidents
 * @param {IncidentFilters} filters
 * @returns {Promise<number>}
 */
export async function getResolvedIncidentCount(filters = {}) {
  const incidents = await getAllIncidents(filters);
  return incidents.filter(i => !isIncidentOpen(i)).length;
}

/**
 * Get count of critical incidents
 * @param {IncidentFilters} filters
 * @returns {Promise<number>}
 */
export async function getCriticalIncidentCount(filters = {}) {
  const incidents = await getAllIncidents({ ...filters, severity: SEVERITY.CRITICAL });
  return incidents.length;
}

/**
 * Get failed payment incidents
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getFailedPaymentIncidents(additionalFilters = {}) {
  return getAllIncidents({
    ...additionalFilters,
    domain: DOMAINS.PAYMENTS,
    unresolvedOnly: true
  });
}

/**
 * Get blocked/conflict incidents
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getBlockedIncidents(additionalFilters = {}) {
  const incidents = await getAllIncidents(additionalFilters);
  return incidents.filter(i => 
    i.status === INCIDENT_STATUS.CONFLICT || 
    i.status === INCIDENT_STATUS.FAILED ||
    i.followUpStatus === FOLLOWUP_STATUS.NEEDS_FOLLOWUP
  );
}

/**
 * Get incidents needing attention (failed, conflict, needs review)
 * @param {IncidentFilters} additionalFilters
 * @returns {Promise<Array>}
 */
export async function getIncidentsNeedingAttention(additionalFilters = {}) {
  const incidents = await getAllIncidents(additionalFilters);
  return incidents.filter(i => 
    i.status === INCIDENT_STATUS.FAILED ||
    i.status === INCIDENT_STATUS.CONFLICT ||
    i.status === INCIDENT_STATUS.MANUAL_REVIEW ||
    i.followUpStatus === FOLLOWUP_STATUS.NEEDS_FOLLOWUP ||
    i.followUpStatus === FOLLOWUP_STATUS.ESCALATED
  );
}

/**
 * Get unique filter options from incidents
 * @returns {Promise<Object>}
 */
export async function getFilterOptions() {
  const incidents = await getCachedIncidents();
  
  const locations = new Set();
  const terminals = new Set();
  const actors = new Map(); // userId -> name
  const domains = new Set();
  const sources = new Set();
  
  for (const incident of incidents) {
    if (incident.locationId) locations.add(incident.locationId);
    if (incident.terminalId) terminals.add(incident.terminalId);
    if (incident.actor?.userId) {
      actors.set(incident.actor.userId, incident.actor.name || incident.actor.userId);
    }
    if (incident.domain) domains.add(incident.domain);
    if (incident.sourceType) sources.add(incident.sourceType);
  }
  
  return {
    locations: Array.from(locations).sort(),
    terminals: Array.from(terminals).sort(),
    actors: Array.from(actors.entries()).map(([id, name]) => ({ id, name })),
    domains: Array.from(domains).sort(),
    sourceTypes: Array.from(sources).sort(),
    severities: Object.values(SEVERITY),
    statuses: Object.values(INCIDENT_STATUS),
    followUpStatuses: Object.values(FOLLOWUP_STATUS)
  };
}

export default {
  // Filter/Sort helpers
  applyFilters,
  sortIncidents,
  groupIncidents,
  groupIncidentsByTimeBucket,
  SORT_OPTIONS,
  
  // Selector functions
  getAllIncidents,
  getIncidentById,
  getIncidentsByDomain,
  getIncidentsBySeverity,
  getIncidentsByLocation,
  getIncidentsByTerminal,
  getIncidentsByActor,
  getIncidentTimeline,
  getOpenIncidentCount,
  getResolvedIncidentCount,
  getCriticalIncidentCount,
  getFailedPaymentIncidents,
  getBlockedIncidents,
  getIncidentsNeedingAttention,
  getFilterOptions
};
